import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    TextField,
} from '../../components';
import { IllustrationScribblingMiniature } from '../../icons';
import { ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseData, ExerciseViewProps } from '../exercises';

export const exercise222Page2TextKey = 'exercise222Page2TextKey';
const dataKeys: string[] = [exercise222Page2TextKey];

const page2TextPlaceholders = [
    'F.eks. “gå en tur”',
    'F.eks. “ringe til en ven”',
    'F.eks. “høre musik”',
];

export const exerciseData222: ExerciseData = {
    ExerciseView: Module2S2Exercise2,
    dataKeys,
    questions: [
        createQuestionFromConfiguration({
            title: 'Din liste over selvomsorg',
            description:
                'På din liste kan der stå aktiviteter, der plejer at få dig til at få det rart. Noget hvor du ikke skal præstere, og hvor du kan være dig selv.',
            dataKeys,
            isArray: true,
        }),
    ],
};

export function Module2S2Exercise2({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Liste over selvomsorg">
            <ProgressBar current={exercise.CurrentPage} total={3} />
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            savedData={JSON.parse(
                                exercise.Data[exercise222Page2TextKey] ?? '[]',
                            )}
                            onContinue={(data) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [exercise222Page2TextKey]:
                                            JSON.stringify(
                                                data
                                                    .map(normalizeInput)
                                                    .filter(Boolean),
                                            ),
                                    },
                                });
                            }}
                        />
                    ),
                    3: (
                        <Page3
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '2.2.2.completed',
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

type PageProps = {
    onContinue: () => void;
};

function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2>Lav en liste over selvomsorg</LabelDisplay2>
            <LabelBody class="my-10">
                At drage omsorg for sig selv kan virke beroligende og være en
                måde at få overtænkningen og selvkritikken til ikke at tage
                over.
            </LabelBody>
            <LabelBody>
                I øvelsen laver du din egen liste med ting, du godt kan lide at
                lave. Listen kan du tage frem, hvis du bliver fanget af
                ubehagelige følelser og tanker.
            </LabelBody>
            <SwirlyBox class="my-30">
                Når du er klar til at lave din liste, kan du gå videre.
            </SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Gå i gang</SecondaryButton>
        </>
    );
}

type Page2Data = string[];
type Page2Props = {
    savedData: Page2Data;
    onContinue: (data: Page2Data) => void;
};

function Page2({ savedData, onContinue }: Page2Props) {
    while (savedData.length < 3) {
        savedData.push('');
    }
    const [texts, setTexts] = useSyncedState(savedData);

    return (
        <>
            <Card class="mb-30">
                <LabelDisplay1 class="mb-10">
                    Din liste over selvomsorg
                </LabelDisplay1>
                <LabelBody>
                    På din liste kan der stå aktiviteter, der plejer at få dig
                    til at få det rart. Noget hvor du ikke skal præstere, og
                    hvor du kan være dig selv. F.eks. gå en tur, ringe til en
                    ven, bage en kage, se en film, dyrke sport, høre musik.
                </LabelBody>
                {texts.map((x, idx) => (
                    <TextField
                        key={idx}
                        className="mt-30"
                        placeholder={page2TextPlaceholders[idx]}
                        text={x}
                        onChangeText={(e) => {
                            const copy = texts.slice();
                            copy[idx] = e;
                            setTexts(copy);
                        }}
                    />
                ))}
                <GhostButton
                    class="my-30"
                    onClick={() => {
                        setTexts(texts.concat(''));
                    }}
                >
                    Tilføj mere
                </GhostButton>
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue(texts);
                }}
            >
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

function Page3({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay1 class="mb-10">Afrunding</LabelDisplay1>
            <LabelBody>
                En konkret liste over selvomsorg kan gøre det nemmere at huske
                at få prioriteret det i de svære øjeblikke. Udover at du kan
                finde din liste i logbogen, kan der være mange andre måder, du
                kan huske dig selv på det. Nogen sætter en reminder på sin
                telefon, andre hænger en post-it på spejlet. Det vigtigste er,
                at du husker at tage dig af dig selv.
            </LabelBody>
            <IllustrationScribblingMiniature className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
